import { useEffect, useMemo, useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Activity,
  BarChart3,
  BedDouble,
  Building2,
  CalendarCheck,
  ChevronDown,
  CircleDollarSign,
  FileCode2,
  FileSpreadsheet,
  FileText,
  KeyRound,
  Landmark,
  LayoutDashboard,
  LogOut,
  PanelLeftClose,
  PanelLeftOpen,
  Receipt,
  SearchCheck,
  Settings,
  UserRound,
  Users,
  WalletCards,
} from "lucide-react";
import { logout } from "../../redux/userSlice";
import { confirmAction, notify } from "../../utils/toastAlert";
import { fileUrl } from "../../utils/files";
import logo from "../../assets/img/logo.png";

const groups = [
  {
    title: "Definições",
    items: [
      { label: "Perfil da Empresa", path: "/dashboard/settings/company", icon: Building2 },
      { label: "Perfil do Utilizador", path: "/dashboard/profile", icon: UserRound },
      { label: "Utilizadores", path: "/dashboard/settings/users", icon: Users },
      { label: "Bancos", path: "/dashboard/settings/bancos", icon: Landmark },
      { label: "Séries", path: "/dashboard/settings/series", icon: KeyRound },
      { label: "Serviços", path: "/dashboard/settings/servicos", icon: CircleDollarSign },
      { label: "Quartos", path: "/dashboard/settings/quartos", icon: BedDouble },
    ],
  },
  {
    title: "Comercial",
    items: [
      { label: "Clientes", path: "/comercial/clientes", icon: Users },
      { label: "Documentos", path: "/comercial/documentos", icon: FileText },
      { label: "Consultar Factura", path: "/comercial/comercial/consultar-factura", icon: SearchCheck },
      { label: "Registar Despesa", path: "/comercial/comercial/registar-despesa", icon: WalletCards },
    ],
  },
  {
    title: "Relatórios",
    items: [
      { label: "Relatórios PDF", path: "/relatorios/pdf", icon: FileText },
      { label: "Fluxo de Caixa", path: "/relatorios/fluxo-caixa", icon: BarChart3 },
      { label: "SAFT Contabilidade", path: "/relatorios/saft-contabilidade", icon: FileCode2 },
      { label: "Conta Corrente", path: "/relatorios/conta-corrente", icon: Receipt },
      { label: "Produtividade", path: "/relatorios/produtividade", icon: Activity },
      { label: "Fecho Diário", path: "/relatorios/fecho-diario", icon: FileSpreadsheet },
      { label: "Check-In", path: "/relatorios/check-in", icon: CalendarCheck },
    ],
  },
];

const initials = (value = "") => {
  const words = value.trim().split(/\s+/).slice(0, 2);
  return words.map((word) => word[0]).join("").toUpperCase() || "U";
};

const roleLabel = (nivel) => {
  if (nivel === "admin") return "Administrador";
  if (nivel === "caixa") return "Caixa";
  return "Utilizador";
};

const DashboardLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, empresa } = useSelector((state) => state.user);
  const isCaixa = user?.nivel === "caixa";
  const homePath = isCaixa ? "/comercial/documentos" : "/dashboard/main";
  const visibleGroups = useMemo(() => {
    if (!isCaixa) return groups;

    return groups
      .map((group) => {
        if (group.title === "Definições") {
          return { ...group, items: group.items.filter((item) => item.path === "/dashboard/profile") };
        }

        if (group.title === "Comercial") {
          return { ...group, items: group.items.filter((item) => ["/comercial/documentos", "/comercial/comercial/consultar-factura"].includes(item.path)) };
        }

        if (group.title === "Relatórios") {
          return { ...group, items: group.items.filter((item) => item.path === "/relatorios/fecho-diario") };
        }

        return { ...group, items: [] };
      })
      .filter((group) => group.items.length > 0);
  }, [isCaixa]);
  const [collapsed, setCollapsed] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => {
    const initial = {};
    groups.forEach((group) => {
      initial[group.title] = true;
    });
    return initial;
  });

  const activeGroup = useMemo(() => {
    return visibleGroups.find((group) => group.items.some((item) => location.pathname === item.path))?.title;
  }, [location.pathname, visibleGroups]);

  useEffect(() => {
    const caixaAllowedPaths = [
      "/comercial/documentos",
      "/comercial/documentos-emitidos",
      "/comercial/comercial/consultar-factura",
      "/dashboard/profile",
      "/relatorios/fecho-diario",
    ];

    if (isCaixa && !caixaAllowedPaths.includes(location.pathname)) {
      navigate(homePath, { replace: true });
    }
  }, [homePath, isCaixa, location.pathname, navigate]);

  const handleLogout = async () => {
    const result = await confirmAction({
      title: "Terminar sessão",
      text: "Deseja realmente terminar a sessão?",
      icon: "question",
      confirmButtonText: "Sim, terminar",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    await dispatch(logout());
    await notify("Sessão terminada com sucesso.", "success");
    navigate("/login", { replace: true });
  };

  const toggleGroup = (title) => {
    if (collapsed) {
      setCollapsed(false);
      setOpenGroups((current) => ({ ...current, [title]: true }));
      return;
    }

    setOpenGroups((current) => ({ ...current, [title]: !current[title] }));
  };

  return (
    <div className={`app-shell ${collapsed ? "app-shell-collapsed" : ""}`}>
      <aside className="app-sidebar">
        <div className="app-sidebar-brand">
          <Link className="app-logo" to={homePath} title="Facturize">
            <img src={logo} alt="Facturize" />
          </Link>
          <button className="app-sidebar-toggle" type="button" onClick={() => setCollapsed((value) => !value)} title={collapsed ? "Expandir menu" : "Recolher menu"}>
            {collapsed ? <PanelLeftOpen size={18} /> : <PanelLeftClose size={18} />}
          </button>
        </div>

        <nav className="app-sidebar-nav" aria-label="Menu principal">
          {!isCaixa && (
            <NavLink className="app-nav-link" to="/dashboard/main" title="Visão Geral">
              <span className="app-nav-icon"><LayoutDashboard size={18} /></span>
              <span className="app-nav-text">Visão Geral</span>
            </NavLink>
          )}

          {visibleGroups.map((group) => {
            const isOpen = openGroups[group.title] || activeGroup === group.title;
            return (
              <div className={`app-menu-group ${isOpen ? "is-open" : ""}`} key={group.title}>
                <button className="app-menu-group-button" type="button" onClick={() => toggleGroup(group.title)} title={group.title}>
                  <span className="app-nav-text">{group.title}</span>
                  <ChevronDown size={16} className="app-menu-chevron" />
                </button>

                <div className="app-menu-group-items">
                  {group.items.map((item) => {
                    const Icon = item.icon || Settings;
                    return (
                      <NavLink className="app-nav-link app-nav-link-child" to={item.path} key={`${group.title}-${item.label}`} title={item.label}>
                        <span className="app-nav-icon"><Icon size={17} /></span>
                        <span className="app-nav-text">{item.label}</span>
                      </NavLink>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </nav>
      </aside>

      <section className="app-main">
        <header className="app-header">
          <div className="app-header-left">
            <button className="app-header-menu" type="button" onClick={() => setCollapsed((value) => !value)} title="Alternar menu">
              {collapsed ? <PanelLeftOpen size={20} /> : <PanelLeftClose size={20} />}
            </button>
            <div>
              <h1>{empresa?.nome || "Empresa"}</h1>
              <p>Gestão comercial, fiscal e operacional</p>
            </div>
          </div>

          <div className="app-header-user">
            {user?.foto ? (
              <img className="app-user-photo" src={fileUrl(user.foto)} alt={user.nome || "Utilizador"} />
            ) : (
              <div className="app-user-avatar">{initials(user?.nome)}</div>
            )}
            <div className="app-user-meta">
              <strong>{user?.nome || "Utilizador"}</strong>
              <span>{roleLabel(user?.nivel)}</span>
            </div>
            <button className="app-logout-button" onClick={handleLogout} type="button">
              <LogOut size={16} />
              Sair
            </button>
          </div>
        </header>

        <main className="app-main-content">
          <Outlet />
        </main>

        <footer className="app-footer">
          <span>© Facturize ERP (FE/238/AGT/2026) - Módulo de Hotelaria</span>
          <span>Desenvolvido Por Núcleo Lógico - Comércio e Serviços (SU), LDA</span>
        </footer>
      </section>
    </div>
  );
};

export default DashboardLayout;
